import { useEffect, useRef, useState } from "react";
import { authClient } from "@/lib/auth";
import { BOOT_SEARCH } from "@/lib/bootSearch";
import { useBranding } from "@/lib/branding";
import { api } from "@/lib/api";
import { Icon } from "@/components/Icon";
import { Button } from "@/components/Button";
import logoMark from "@/assets/logo-mark.svg";

/**
 * S1 Login — reconciled with designs/Login.html.
 *
 * Two-column layout: brand panel left (logo + wordmark + tagline + hero
 * headline + paragraph + foot), form card right
 * (eyebrow + h2 + desc + field stack). Two stages: email entry → OTP entry.
 *
 * Structural classes mirror the design: `.auth`, `.brandside`, `.bs-top`,
 * `.bs-mid`, `.bs-foot`, `.formside`, `.form-card`, `.eyebrow`,
 * `.desc`, `.input-search`, `.or`, `.legal`, `.otp-row`, `.back-link`,
 * `.resend`. The data plumbing (better-auth OTP send/verify, invite
 * preview) is unchanged.
 */
export function Login() {
  const { orgName, logoUrl, loginTagline, loginHeroHeadline, loginHeroText } =
    useBranding();
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [stage, setStage] = useState<"email" | "otp">("email");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [invitedBy, setInvitedBy] = useState<string | null>(null);
  const [inviteExpired, setInviteExpired] = useState(false);

  const otpFull = otp.every((c) => c.length === 1);

  useEffect(() => {
    if (stage === "otp") otpRefs.current[0]?.focus();
  }, [stage]);

  // Read ?invite=<token> from the URL and resolve it via the public
  // preview endpoint. Valid → pre-fill email + show "Invited by …" banner.
  // 404 → render an "invite expired / revoked" notice, but leave the
  // form usable so a returning user can still sign in with their own email.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("invite");
    if (!token) return;
    let cancelled = false;
    (async () => {
      const preview = await api.inviteePreview(token);
      if (cancelled) return;
      if (!preview) {
        setInviteExpired(true);
        return;
      }
      setEmail(preview.email);
      setInvitedBy(preview.invitedBy);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Where to go after a successful sign-in. Normally the dashboard, but if we
  // arrived here mid-OAuth — better-auth's MCP `authorize` endpoint redirects
  // an unauthenticated user to /login with the OAuth query string preserved —
  // return to the authorize endpoint now that we have a session so the grant
  // completes and the AI client (Claude Desktop / ChatGPT) gets its code.
  function postLoginDest(): string {
    // BOOT_SEARCH is the query string captured at app boot, before the router
    // stripped the OAuth params from /login?response_type=...&client_id=...
    const search = BOOT_SEARCH;
    const params = new URLSearchParams(search);
    if (
      params.get("response_type") &&
      params.get("client_id") &&
      params.get("redirect_uri")
    ) {
      return `/api/auth/mcp/authorize${search}`;
    }
    return "/";
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (stage === "email") {
        const r = await authClient.emailOtp.sendVerificationOtp({
          email,
          type: "sign-in",
        });
        if (r.error) {
          setError(r.error.message ?? "Unknown error");
          return;
        }
        setStage("otp");
      } else {
        await verifyOtp(otp.join(""));
      }
    } finally {
      setSubmitting(false);
    }
  }

  // Verify a 6-digit code and redirect. Shared by the form submit and the
  // auto-submit on a complete clipboard paste.
  async function verifyOtp(code: string) {
    const r = await authClient.signIn.emailOtp({ email, otp: code });
    if (r.error) {
      setError(r.error.message ?? "Unknown error");
      return;
    }
    window.location.href = postLoginDest();
  }

  function setOtpDigit(idx: number, value: string) {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...otp];
    next[idx] = digit;
    setOtp(next);
    if (digit && idx < 5) otpRefs.current[idx + 1]?.focus();
  }

  function onOtpPaste(e: React.ClipboardEvent<HTMLInputElement>, idx = 0) {
    const text = e.clipboardData.getData("text").replace(/\D/g, "");
    if (text.length === 0) return;
    e.preventDefault();
    // Fill from the box that received the paste; a full 6-digit code pasted
    // into any box lays out across all six.
    const next = [...otp];
    const start = text.length >= 6 ? 0 : idx;
    const usable = text.slice(0, 6 - start);
    for (let i = 0; i < usable.length; i++) next[start + i] = usable[i]!;
    setOtp(next);
    const filledTo = Math.min(start + usable.length, 5);
    otpRefs.current[filledTo]?.focus();
    // Auto-submit the moment all six are present.
    if (next.every((d) => d.length === 1)) {
      void verifyOtp(next.join(""));
    }
  }

  function onOtpKey(e: React.KeyboardEvent<HTMLInputElement>, idx: number) {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      otpRefs.current[idx - 1]?.focus();
    }
  }

  return (
    <div className="auth bui">
      <div className="brandside">
        <div className="bs-top">
          <img src={logoUrl || logoMark} alt={orgName} />
          <div>
            <b>{orgName}</b>
            <span>{loginTagline}</span>
          </div>
        </div>
        <div className="bs-mid">
          <h1>{loginHeroHeadline}</h1>
          <p>{loginHeroText}</p>
        </div>
        <div className="bs-foot">
          © {new Date().getFullYear()} {orgName}
        </div>
      </div>
      <div className="formside">
        {stage === "email" ? (
          <form className="form-card" onSubmit={onSubmit}>
            <div className="eyebrow">
              {invitedBy ? "Welcome" : "Welcome back"}
            </div>
            <h2>{invitedBy ? "You're invited" : `Sign in to ${orgName}`}</h2>
            <p className="desc">
              {invitedBy ? (
                <>
                  <b>{invitedBy}</b> invited you. Use your work email and we'll
                  send you a one-time code — no password to remember.
                </>
              ) : (
                <>
                  Use your work email and we'll send you a one-time code — no
                  password to remember.
                </>
              )}
            </p>
            {inviteExpired && (
              <p
                role="alert"
                className="t-sm"
                style={{
                  background: "var(--st-warn-bg, #fff7e6)",
                  border: "1px solid var(--st-warn-border, #f0c33c)",
                  color: "var(--st-warn-ink, #6a4f00)",
                  padding: "8px 10px",
                  borderRadius: "var(--r-md)",
                  margin: "0 0 12px",
                }}
              >
                This invite has expired or been revoked. If you already have an
                account, sign in below; otherwise ask your admin to send a new
                one.
              </p>
            )}
            <div className="field" style={{ marginBottom: 14 }}>
              <label htmlFor="email">Work email</label>
              <div className="input-search">
                <Icon name="mail" size={15} />
                <input
                  id="email"
                  className="input"
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>
            </div>
            {error && (
              <p
                role="alert"
                style={{
                  color: "var(--st-danger-ink)",
                  fontSize: 13,
                  margin: "0 0 12px",
                }}
              >
                {error}
              </p>
            )}
            <Button
              type="submit"
              variant="primary"
              block
              disabled={submitting || !email}
            >
              {submitting ? "Sending…" : "Continue with email"}
            </Button>
            {typeof authClient.signIn?.passkey === "function" && (
              <>
                <div className="or">or</div>
                <Button
                  type="button"
                  variant="secondary"
                  block
                  disabled={submitting}
                  onClick={async () => {
                    setError(null);
                    setSubmitting(true);
                    try {
                      const r = await authClient.signIn.passkey();
                      if (r?.error) {
                        setError(
                          r.error.message ??
                            "No passkey found for this device.",
                        );
                        return;
                      }
                      window.location.href = postLoginDest();
                    } catch (e) {
                      setError(
                        (e as Error)?.message ??
                          "Couldn't sign in with a passkey.",
                      );
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                >
                  <Icon name="key" size={17} />
                  Sign in with a passkey
                </Button>
              </>
            )}
            <div className="legal">
              By continuing you agree to {orgName}'s{" "}
              <a href="/legal/terms">Terms</a> &amp;{" "}
              <a href="/legal/privacy">Privacy Policy</a>.
            </div>
          </form>
        ) : (
          <form className="form-card" onSubmit={onSubmit}>
            <button
              type="button"
              className="back-link"
              onClick={() => {
                setStage("email");
                setOtp(["", "", "", "", "", ""]);
                setError(null);
              }}
            >
              <Icon name="arrow" />
              Back
            </button>
            <div className="eyebrow">Check your inbox</div>
            <h2>Enter your code</h2>
            <p className="desc">
              We sent a 6-digit code to <b>{email}</b>. It expires in 10
              minutes.
            </p>
            <div className="otp-row">
              {otp.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    otpRefs.current[i] = el;
                  }}
                  value={d}
                  inputMode="numeric"
                  maxLength={1}
                  onChange={(e) => setOtpDigit(i, e.target.value)}
                  onPaste={(e) => onOtpPaste(e, i)}
                  onKeyDown={(e) => onOtpKey(e, i)}
                  aria-label={`Digit ${i + 1}`}
                />
              ))}
            </div>
            {error && (
              <p
                role="alert"
                style={{
                  color: "var(--st-danger-ink)",
                  fontSize: 13,
                  margin: "8px 0 0",
                }}
              >
                {error}
              </p>
            )}
            <Button
              type="submit"
              variant="primary"
              block
              disabled={submitting || !otpFull}
              style={{ marginTop: 14 }}
            >
              {submitting ? "Verifying…" : "Verify & continue"}
            </Button>
            <p className="resend">
              Didn't get it?{" "}
              <a
                onClick={() => {
                  setStage("email");
                  setOtp(["", "", "", "", "", ""]);
                }}
              >
                Resend code
              </a>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
